import { contractRequest } from "@/portals/clinician/api/transport";
import type { OpenReviewItemStatus, RecordEffect, RecordReview, ReviewTriggerType } from "@/features/patient/records/api/dataPipelineApi";

// Module 2/3 patient review items. Every call takes the acting profile the same
// way dataPipelineApi does (X-Acting-As-Profile-Id, own dependents only), and
// every failure goes through the one contract error parser (contractErrors.ts).

export interface ReviewItemsCount {
  awaitingInput: number;
}

export type ReviewItemStatus = OpenReviewItemStatus | "RESOLVED_PATIENT_REPORTED" | "RESOLVED_CLINICIAN_VERIFIED";

export type ReviewAnswer =
  | "CURRENT_MEDICINE"
  | "NO_LONGER_TAKE"
  | "DOSE_CHANGED"
  | "TAKE_BOTH"
  | "NOT_RECOGNIZED"
  | "NOT_SURE"
  | "SKIPPED"
  /** A free-text reply to the clinician's question (the comment is the reply, required). */
  | "REPLY";

export interface ReviewItemRecord {
  id: string;
  recordRole: RecordReview["recordRole"];
  sourceVendor: string;
  createdAt: string;
  /** On a CORRECTED record, already contains the corrected values. */
  fields: Record<string, unknown>;
  /** Module 5: CORRECTED records only — which keys were corrected (sorted). */
  correctedFields?: string[];
  /** The RxNorm code (RxCUI) it was matched to; null when none was found. */
  rxcui?: string | null;
}

export interface PatientResponse {
  answer: ReviewAnswer;
  comment: string | null;
  respondedAt: string;
}

export type DecisionAction =
  | "KEEP_EXISTING"
  | "ACCEPT_NEW"
  | "BOTH_VALID"
  | "MARK_DISCONTINUED"
  | "CORRECT_VALUE"
  | "REJECT_BOTH"
  | "REQUEST_MORE_INFO";

/** Module 5 — set only when itemStatus is RESOLVED_CLINICIAN_VERIFIED. */
export interface ReviewOutcome {
  action: Exclude<DecisionAction, "REQUEST_MORE_INFO">;
  reason: string;
  decidedAt: string;
  effects: { recordId: string; effect: RecordEffect }[];
}

export interface ReviewItem {
  id: string;
  triggerType: ReviewTriggerType;
  itemStatus: ReviewItemStatus;
  createdAt: string;
  /** true only for AWAITING_PATIENT / NEEDS_MORE_INFO */
  awaitingInput: boolean;
  /** Waiting on the patient, or already with a clinician but never answered. */
  canRespond?: boolean;
  records: ReviewItemRecord[];
  /** Latest clinician question, only while itemStatus is NEEDS_MORE_INFO. */
  clinicianQuestion: { text: string; askedAt: string } | null;
  /** Every question the clinician asked, oldest first (the whole Patient Conversation). */
  clinicianQuestions?: { text: string; askedAt: string }[];
  /** Oldest first. */
  patientResponses: PatientResponse[];
  /** Module 5 — the clinician's decision, once RESOLVED_CLINICIAN_VERIFIED. Optional for older backends. */
  outcome?: ReviewOutcome | null;
}

export const COMMENT_MAX_LENGTH = 1000;

const actingAs = (actingAsProfileId?: string) =>
  actingAsProfileId ? { "X-Acting-As-Profile-Id": actingAsProfileId } : undefined;

/**
 * GET /api/patient/review-items/count — open medication review items awaiting the
 * patient's input. Pass a Dedicated Profile's id to count that dependent's items.
 */
export const getReviewItemsCountApi = (actingAsProfileId?: string) =>
  contractRequest<ReviewItemsCount>("/api/patient/review-items/count", {
    method: "GET",
    portal: "patient",
    headers: actingAs(actingAsProfileId),
  });

/**
 * New records still being worked on: "reading" = a document upload is still
 * being read; "checking" = its medicines are being matched with RxNorm;
 * "comparing" = all matched, the conflict check with the patient's other
 * records is running; "idle" = nothing running.
 */
export interface ReviewProcessing {
  state: "idle" | "reading" | "checking" | "comparing";
  uploadsInProgress: number;
  medicationsChecked: number;
  medicationsTotal: number;
}

/** GET /api/patient/review-items/processing */
export const getReviewProcessingApi = (actingAsProfileId?: string) =>
  contractRequest<ReviewProcessing>("/api/patient/review-items/processing", {
    method: "GET",
    portal: "patient",
    headers: actingAs(actingAsProfileId),
  });

/** Newest first. "open" (default) excludes RESOLVED_* items. */
export const listReviewItemsApi = (state: "open" | "all" = "open", actingAsProfileId?: string) =>
  contractRequest<{ items: ReviewItem[] }>(`/api/patient/review-items?state=${state}`, {
    method: "GET",
    portal: "patient",
    headers: actingAs(actingAsProfileId),
  });

/** 404 REVIEW_ITEM_NOT_FOUND */
export const getReviewItemApi = (id: string, actingAsProfileId?: string) =>
  contractRequest<ReviewItem>(`/api/patient/review-items/${encodeURIComponent(id)}`, {
    method: "GET",
    portal: "patient",
    headers: actingAs(actingAsProfileId),
  });

/** 201 updated item · 404 REVIEW_ITEM_NOT_FOUND · 409 ITEM_NOT_AWAITING_INPUT · 400 VALIDATION_ERROR */
export const submitReviewResponseApi = (
  id: string,
  body: { answer: ReviewAnswer; comment?: string },
  actingAsProfileId?: string,
) =>
  contractRequest<ReviewItem>(`/api/patient/review-items/${encodeURIComponent(id)}/responses`, {
    method: "POST",
    portal: "patient",
    body,
    headers: actingAs(actingAsProfileId),
  });
